import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Gamepad2, Save, Loader2, Trophy, Check, Brain, Type, Hash, Ticket, Search,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GAME_META = {
  hangman:  { label: 'Hangman',       desc: '5 levels · 3 hints per word', Icon: Hash,  color: '#FF007F' },
  trivia:   { label: 'Indian Trivia', desc: '3 categories — politics, sports, movies', Icon: Brain, color: '#39FF14' },
  scramble: { label: 'Word Scramble', desc: '5 scrambled words (7–10 letters)', Icon: Type, color: '#FFD700' },
};

const formatTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch { return iso; }
};

const GamesAdminPage = () => {
  const { user } = useAuth();
  const [rewards, setRewards] = useState({ hangman: '', trivia: '', scramble: '' });
  const [coupons, setCoupons] = useState([]);
  const [stats, setStats] = useState({ total: 0, redeemed: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [redeemBusy, setRedeemBusy] = useState(null);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, k] = await Promise.all([
        axios.get(`${API}/games/config`),
        axios.get(`${API}/games/coupons`),
      ]);
      setRewards(c.data.rewards || {});
      setCoupons(k.data.coupons || []);
      setStats({ total: k.data.total || 0, redeemed: k.data.redeemed || 0 });
    } catch (err) {
      console.error('[GamesAdmin] load failed:', err);
    }
    setLoading(false);
    setDirty(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateReward = useCallback((key, value) => {
    setRewards((r) => ({ ...r, [key]: value }));
    setDirty(true);
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/games/config`, { rewards });
      setRewards(r.data.rewards || {});
      setDirty(false);
      toast.success('Saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  }, [rewards]);

  const redeem = useCallback(async (code) => {
    setRedeemBusy(code);
    try {
      const r = await axios.post(`${API}/games/coupons/${code}/redeem`);
      setCoupons((prev) => prev.map((c) => (c.code === code ? r.data : c)));
      setStats((s) => ({ ...s, redeemed: s.redeemed + 1 }));
      toast.success(`Redeemed ${code}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not redeem');
    } finally {
      setRedeemBusy(null);
    }
  }, []);

  const LayoutWrapper = user?.role === 'superadmin' ? AdminLayout : Layout;

  const filteredCoupons = filter.trim()
    ? coupons.filter((c) => {
        const q = filter.toLowerCase();
        return (
          (c.code || '').toLowerCase().includes(q) ||
          (c.guest_name || '').toLowerCase().includes(q) ||
          (c.guest_phone || '').toLowerCase().includes(q) ||
          (c.game || '').toLowerCase().includes(q)
        );
      })
    : coupons;

  if (loading) {
    return <LayoutWrapper><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-[#FF007F] animate-spin" /></div></LayoutWrapper>;
  }

  return (
    <LayoutWrapper>
      <div className="p-4 lg:p-8 pb-24 max-w-6xl mx-auto" data-testid="games-admin-page">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6 lg:mb-8">
          <div className="flex items-center gap-3">
            <Gamepad2 className="w-6 h-6 lg:w-7 lg:h-7 text-[#FF007F]" />
            <div>
              <h1 className="text-2xl lg:text-3xl font-extrabold text-white tracking-tight font-['Outfit']">Bar Games</h1>
              <p className="text-gray-400 text-xs lg:text-sm">Set rewards per game and manage redemptions.</p>
            </div>
          </div>
          {dirty && (
            <Button onClick={save} disabled={saving} className="bg-[#FF007F] hover:bg-[#CC0066] text-white rounded-full px-5 text-xs font-bold tracking-wider" data-testid="games-save">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} SAVE
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
          {/* Rewards */}
          <div className="bg-white/[0.03] border border-white/10 rounded-3xl p-5 lg:p-6">
            <div className="flex items-center gap-2 mb-4">
              <Trophy className="w-4 h-4 text-[#FFD700]" />
              <h3 className="text-sm font-extrabold text-white uppercase tracking-[0.18em] font-['Outfit']">Rewards per game</h3>
            </div>
            <p className="text-[11px] text-gray-500 mb-4">Guests win one coupon per game per hour.</p>
            <div className="space-y-4">
              {Object.entries(GAME_META).map(([key, meta]) => {
                const Icon = meta.Icon;
                return (
                  <div key={key} className="rounded-2xl bg-black/30 border border-white/5 p-4" data-testid={`games-reward-row-${key}`}>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${meta.color}25` }}>
                        <Icon className="w-4.5 h-4.5" style={{ color: meta.color }} />
                      </div>
                      <div>
                        <p className="text-white text-sm font-bold font-['Outfit']">{meta.label}</p>
                        <p className="text-gray-500 text-[11px]">{meta.desc}</p>
                      </div>
                    </div>
                    <Input
                      value={rewards[key] || ''}
                      onChange={(e) => updateReward(key, e.target.value.slice(0, 200))}
                      placeholder={`e.g. ${key === 'hangman' ? 'Free shot at the bar' : key === 'trivia' ? '10% off your next drink' : 'Complimentary chai'}`}
                      className="bg-black/40 border-white/15 text-white h-10 rounded-xl"
                      data-testid={`games-reward-input-${key}`}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Coupons */}
          <div className="bg-white/[0.03] border border-white/10 rounded-3xl p-5 lg:p-6" data-testid="games-coupons-card">
            <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <Ticket className="w-4 h-4 text-[#39FF14]" />
                <h3 className="text-sm font-extrabold text-white uppercase tracking-[0.18em] font-['Outfit']">Coupons</h3>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-[#FF007F]/15 text-[#FF007F]" data-testid="games-coupons-total">
                  {stats.total} issued
                </span>
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-[#39FF14]/15 text-[#39FF14]" data-testid="games-coupons-redeemed">
                  {stats.redeemed} redeemed
                </span>
              </div>
            </div>

            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
              <Input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Search by code, name, phone, or game..."
                className="pl-9 bg-black/40 border-white/10 text-white h-9 rounded-lg text-xs"
                data-testid="games-coupons-search"
              />
            </div>

            <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
              {filteredCoupons.length === 0 ? (
                <p className="text-gray-500 text-xs text-center py-10">No coupons yet. Guests will appear here after they win.</p>
              ) : (
                filteredCoupons.map((c) => {
                  const meta = GAME_META[c.game] || {};
                  return (
                    <div
                      key={c.id}
                      className={`p-3 rounded-xl border flex items-center justify-between gap-3 ${
                        c.redeemed ? 'bg-[#39FF14]/5 border-[#39FF14]/20 opacity-80' : 'bg-white/[0.03] border-white/10'
                      }`}
                      data-testid={`games-coupon-row-${c.code}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono font-bold text-white text-sm tracking-wider">{c.code}</span>
                          <span
                            className="text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-wider"
                            style={{ backgroundColor: `${meta.color || '#888'}22`, color: meta.color || '#aaa' }}
                          >
                            {c.game}
                          </span>
                          {c.redeemed && <span className="text-[10px] font-bold uppercase tracking-wider text-[#39FF14]">Redeemed</span>}
                        </div>
                        <p className="text-gray-300 text-xs truncate">{c.reward}</p>
                        <p className="text-gray-500 text-[10px] truncate">
                          {(c.guest_name || 'Anonymous')}{c.guest_phone ? ` · ${c.guest_phone}` : ''} · {formatTime(c.created_at)}
                        </p>
                      </div>
                      {!c.redeemed && (
                        <Button
                          onClick={() => redeem(c.code)}
                          disabled={redeemBusy === c.code}
                          size="sm"
                          className="bg-[#39FF14] hover:bg-[#2dd60f] text-black rounded-full text-[10px] font-bold tracking-wider h-7 px-3 flex-shrink-0"
                          data-testid={`games-coupon-redeem-${c.code}`}
                        >
                          {redeemBusy === c.code ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Check className="w-3 h-3 mr-1" /> REDEEM</>}
                        </Button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </LayoutWrapper>
  );
};

export default GamesAdminPage;
